import { FaroRoutes } from '@grafana/faro-react';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import RequireAuth from './components/RequireAuth';
import ScrollToTop from './components/ScrollToTop';
import SuperuserThemeProvider from './components/SuperuserThemeProvider';
import { FEATURES } from './utils/featureFlags';

// Lazy load pages for performance
const HomePage = lazy(() => import('./pages/HomePage'));
const IndexDetailPage = lazy(() => import('./pages/IndexDetailPage'));
const RatePage = lazy(() => import('./pages/RatePage'));
const ArticlePage = lazy(() => import('./pages/ArticlePage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const IndicesListPage = lazy(() => import('./pages/IndicesListPage'));
const IndexFamilyPage = lazy(() => import('./pages/IndexFamilyPage'));
const CustomIndexPage = lazy(() => import('./pages/CustomIndexPage'));
const ExchangeRelationshipsPage = lazy(() => import('./pages/ExchangeRelationshipsPage'));
const EquityIndicesPage = lazy(() => import('./pages/EquityIndicesPage'));
const EquityIndexPage = lazy(() => import('./pages/EquityIndexPage'));
const RatesListPage = lazy(() => import('./pages/RatesListPage'));
const MarketPage = lazy(() => import('./pages/MarketPage'));
const EmbedPage = lazy(() => import('./pages/EmbedPage'));
const HubEmbedPage = lazy(() => import('./pages/HubEmbedPage'));
const NflGameEmbedPage = lazy(() => import('./pages/NflGameEmbedPage'));
const NflRankingsEmbedPage = lazy(() => import('./pages/NflRankingsEmbedPage'));
const NbaRankingsEmbedPage = lazy(() => import('./pages/NbaRankingsEmbedPage'));
const GovernancePage = lazy(() => import('./pages/GovernancePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LicensingPage = lazy(() => import('./pages/LicensingPage'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const MethodologyPage = lazy(() => import('./pages/MethodologyPage'));
const LabsPage = lazy(() => import('./pages/LabsPage'));
const FilingsListPage = lazy(() => import('./pages/FilingsListPage'));
const FilingPage = lazy(() => import('./pages/FilingPage'));
const SubscribePage = lazy(() => import('./pages/SubscribePage'));
const NflHubPage = lazy(() => import('./pages/NflHubPage'));
const NbaHubPage = lazy(() => import('./pages/NbaHubPage'));
const NflGamePage = lazy(() => import('./pages/NflGamePage'));
const MidtermHubPage = lazy(() => import('./pages/MidtermHubPage'));
const EconomyDashboardPage = lazy(() => import('./pages/EconomyDashboardPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const SuperuserAuditPage = lazy(() => import('./pages/SuperuserAuditPage'));
// The dynamic import has to be gated too, not just the <Route>. `FEATURES.experiments`
// reads the flag through a function call, which the bundler cannot fold, so gating
// only the route still emits the whole experiments chunk, and its source map, whose
// `sourcesContent` carries the original TypeScript, into a build with the flag off.
// Comparing the raw env literal folds to `false` at build time, so the import is
// eliminated and the chunk is never produced. Keep this in sync with `isEnabled`
// in featureFlags.ts, which accepts the same two truthy spellings.
const EXPERIMENTS_BUNDLED =
  import.meta.env.VITE_FEATURE_EXPERIMENTS === 'true' ||
  import.meta.env.VITE_FEATURE_EXPERIMENTS === '1';

const SuperuserBacktestPage = EXPERIMENTS_BUNDLED
  ? lazy(() => import('./pages/SuperuserBacktestPage'))
  : null;

// Legacy path for the experiments page; the redirect keeps deep links
// (view/experiment/compare/source params) working.
function LegacyBacktestsRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/superuser/experiments', search: location.search }} replace />;
}
const SuperuserIndicesPage = lazy(() => import('./pages/SuperuserIndicesPage'));
const SuperuserRatesPage = lazy(() => import('./pages/SuperuserRatesPage'));
const SuperuserEventsPage = lazy(() => import('./pages/SuperuserEventsPage'));
const SuperuserMarketsPage = lazy(() => import('./pages/SuperuserMarketsPage'));
const SuperuserCensusPage = lazy(() => import('./pages/SuperuserCensusPage'));
const SuperuserNewsPage = lazy(() => import('./pages/SuperuserNewsPage'));
const SuperuserFilingsPage = lazy(() => import('./pages/SuperuserFilingsPage'));
const SuperuserOperationsPage = lazy(() => import('./pages/SuperuserOperationsPage'));
const SuperuserHealthPage = lazy(() => import('./pages/SuperuserHealthPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));

// Fullscreen overlay matching the pre-React #adj-splash. Safari 26 samples
// this fixed layer for the toolbar, so it stays the hero green on every route.
const PageLoader = () => (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9998,
      background: '#0e2a1f',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <div className="page-loader-mark">Adjacent</div>
  </div>
);

function AppContent() {
  return (
    <>
      <ScrollToTop />
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <FaroRoutes routesComponent={Routes}>
            {/* Main Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/nfl" element={<NflHubPage />} />
            <Route path="/nba" element={<NbaHubPage />} />
            <Route path="/nfl/game/:gameId" element={<NflGamePage />} />
            <Route path="/midterms" element={<MidtermHubPage />} />
            <Route path="/economy" element={<EconomyDashboardPage />} />
            <Route path="/indices/:slug" element={<IndexDetailPage />} />
            <Route path="/article/:slug" element={<ArticlePage />} />
            <Route path="/markets" element={<DashboardPage />} />
            <Route path="/market/:id" element={<MarketPage />} />
            {/* Hosted, chrome-less embeds for third-party iframes. No Layout,
                header, footer, or auth; just a branded card reading the public
                API. Config rides on the URL query string. */}
            <Route path="/embed/market/:id" element={<EmbedPage kind="market" />} />
            <Route path="/embed/index/:slug" element={<EmbedPage kind="index" />} />
            <Route path="/embed/midterms" element={<HubEmbedPage kind="midterms" />} />
            <Route path="/embed/nfl" element={<HubEmbedPage kind="nfl" />} />
            <Route path="/embed/nba" element={<HubEmbedPage kind="nba" />} />
            <Route path="/embed/nfl/rankings" element={<NflRankingsEmbedPage />} />
            <Route path="/embed/nba/rankings" element={<NbaRankingsEmbedPage />} />
            <Route path="/embed/nfl/game/:gameId" element={<NflGameEmbedPage />} />
            <Route path="/governance" element={<GovernancePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/licensing" element={<LicensingPage />} />
            <Route path="/methodology" element={<MethodologyPage />} />
            <Route path="/compare" element={<ComparePage />} />
            <Route path="/compare/:slug" element={<ComparePage />} />
            <Route path="/labs" element={<LabsPage />} />
            <Route path="/labs/filings" element={<FilingsListPage />} />
            <Route path="/labs/filings/:id" element={<FilingPage />} />
            <Route path="/subscribe" element={<SubscribePage />} />

            {FEATURES.indices && <Route path="/indices" element={<IndicesListPage />} />}
            {FEATURES.indices && (
              <Route path="/indices/family/custom" element={<CustomIndexPage />} />
            )}
            {FEATURES.indices && (
              <Route path="/indices/family/relationships" element={<ExchangeRelationshipsPage />} />
            )}
            {FEATURES.indices && (
              <Route path="/indices/family/equity" element={<EquityIndicesPage />} />
            )}
            {FEATURES.indices && <Route path="/indices/equity/:id" element={<EquityIndexPage />} />}
            {FEATURES.indices && (
              <Route path="/indices/family/:familyId" element={<IndexFamilyPage />} />
            )}
            {FEATURES.rates && <Route path="/rates" element={<RatesListPage />} />}
            {FEATURES.rates && <Route path="/rate/:id" element={<RatePage />} />}

            {/* Bare /superuser lands on the audit page (the default superuser view). */}
            <Route path="/superuser" element={<Navigate to="/superuser/audit" replace />} />
            {/* Superuser: full audit of every index compute cycle's inputs/outputs. */}
            <Route
              path="/superuser/audit"
              element={
                <RequireAuth>
                  <SuperuserAuditPage />
                </RequireAuth>
              }
            />
            {/* Superuser: design, validate, inspect, compare, and promote index
                experiments. Gated by FEATURES.experiments, dev-only until the
                playground has soaked. The legacy path is gated with it so that
                with the flag off it 404s directly instead of redirecting to a
                route that no longer exists. */}
            {FEATURES.experiments && SuperuserBacktestPage && (
              <Route
                path="/superuser/experiments"
                element={
                  <RequireAuth>
                    <SuperuserBacktestPage />
                  </RequireAuth>
                }
              />
            )}
            {/* Legacy route name; preserves query params. */}
            {FEATURES.experiments && (
              <Route path="/superuser/backtests" element={<LegacyBacktestsRedirect />} />
            )}
            {/* Superuser: manage index constituents (bulk add/remove, copy for prod). */}
            <Route
              path="/superuser/indices"
              element={
                <RequireAuth>
                  <SuperuserIndicesPage />
                </RequireAuth>
              }
            />
            {/* Superuser: list, inspect, create, edit, and backfill reference rates. */}
            <Route
              path="/superuser/rates"
              element={
                <RequireAuth>
                  <SuperuserRatesPage />
                </RequireAuth>
              }
            />
            {/* Superuser: tracked events + their child markets and status. */}
            <Route
              path="/superuser/events"
              element={
                <RequireAuth>
                  <SuperuserEventsPage />
                </RequireAuth>
              }
            />
            {/* Superuser: tracked markets: state codes, status, associated data. */}
            <Route
              path="/superuser/markets"
              element={
                <RequireAuth>
                  <SuperuserMarketsPage />
                </RequireAuth>
              }
            />
            {/* Superuser: census state populations + which markets/indices use them. */}
            <Route
              path="/superuser/census"
              element={
                <RequireAuth>
                  <SuperuserCensusPage />
                </RequireAuth>
              }
            />
            {/* Superuser: NewsAPI ingest, articles, filters, quota, embedding
                backlog, and the day-walk backfill trigger. Ported from the
                labs news-v2-dashboard into the catalog pattern. */}
            <Route
              path="/superuser/news"
              element={
                <RequireAuth>
                  <SuperuserNewsPage />
                </RequireAuth>
              }
            />
            {/* Superuser: industry filings catalog, filters, and listings
                backfill. Same catalog pattern as News. */}
            <Route
              path="/superuser/filings"
              element={
                <RequireAuth>
                  <SuperuserFilingsPage />
                </RequireAuth>
              }
            />
            {/* Superuser: the async job queue. Run the whole-system jobs on
                demand and watch every recent run reach a terminal state. */}
            <Route
              path="/superuser/operations"
              element={
                <RequireAuth>
                  <SuperuserOperationsPage />
                </RequireAuth>
              }
            />
            {/* Superuser: scheduled-task and reference-rate freshness in one
                view, over two indexer endpoints that had no consumer. */}
            <Route
              path="/superuser/health"
              element={
                <RequireAuth>
                  <SuperuserHealthPage />
                </RequireAuth>
              }
            />
            {/* Account settings: flat side nav over API keys + embedded Clerk
                profiles. The splat lets Clerk own its path-routed sub-pages
                (e.g. /settings/account/security). */}
            <Route
              path="/settings/*"
              element={
                <RequireAuth>
                  <SettingsPage />
                </RequireAuth>
              }
            />

            <Route path="*" element={<NotFoundPage />} />
          </FaroRoutes>
        </Suspense>
      </ErrorBoundary>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter basename="/">
      <SuperuserThemeProvider>
        <AppContent />
      </SuperuserThemeProvider>
    </BrowserRouter>
  );
}
